import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  fullName: "",
  email: "",
  confirmEmail: "",
  phone: "",
  cityState: "",
  branch: "",
  datesOfService: "",
  mosRateAfsc: "",
  deployments: "",
  filedBefore: "",
  currentRating: "",
  seekingHelp: [],
  conditions: "",
  hasRecords: "",
  receivingVaCare: "",
  timeline: "",
  referredBy: "",
  acknowledgments: [],
  consentToContact: false,
};

const inputCls =
  "w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 focus:ring-0 rounded-sm outline-none transition-colors";

export const IntakeModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const totalSteps = 6;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      if (name === "consentToContact") {
        setFormData({ ...formData, [name]: checked });
      } else if (name === "acknowledgments" || name === "seekingHelp") {
        const arr = formData[name];
        if (checked) {
          setFormData({ ...formData, [name]: [...arr, value] });
        } else {
          setFormData({ ...formData, [name]: arr.filter((v) => v !== value) });
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const nextStep = () => step < totalSteps && setStep(step + 1);
  const prevStep = () => step > 1 && setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.email !== formData.confirmEmail) {
      alert("Email addresses do not match");
      return;
    }
    if (formData.acknowledgments.length < 3 || !formData.consentToContact) {
      alert("Please complete all required acknowledgments and consent");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/intake`, formData);
      setSubmitStatus("success");
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // reset after close animation
    setTimeout(() => {
      setStep(1);
      setSubmitStatus(null);
      setFormData(initialForm);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[60] flex items-start md:items-center justify-center p-4 overflow-y-auto"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto my-4 rounded-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-slate-900 p-6 sticky top-0 z-10">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-serif text-2xl font-bold text-white">
                  Veteran Client Intake Form
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  Step {step} of {totalSteps}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white"
                data-testid="intake-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="mt-4 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#C89B3C] transition-all duration-300"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {submitStatus === "success" ? (
            <div className="p-8 text-center">
              <CheckCircle className="text-green-600 mx-auto mb-4" size={64} />
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">
                Thank You!
              </h3>
              <p className="text-slate-600 mb-6">
                Your intake form has been submitted successfully. We'll review your information
                and contact you within 24–48 hours.
              </p>
              <button
                onClick={handleClose}
                className="bg-slate-900 text-white px-8 py-3 font-medium hover:bg-slate-800 transition-colors"
                data-testid="intake-close-success"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6">
              {step === 1 && <Step1 formData={formData} handleChange={handleChange} />}
              {step === 2 && <Step2 formData={formData} handleChange={handleChange} />}
              {step === 3 && <Step3 formData={formData} handleChange={handleChange} />}
              {step === 4 && <Step4 formData={formData} handleChange={handleChange} />}
              {step === 5 && <Step5 formData={formData} handleChange={handleChange} />}
              {step === 6 && <Step6 formData={formData} handleChange={handleChange} />}

              {/* Nav buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                    data-testid="intake-prev"
                  >
                    <ChevronLeft size={20} /> Previous
                  </button>
                ) : (
                  <div />
                )}

                {step < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 font-medium hover:bg-slate-800 transition-colors"
                    data-testid="intake-next"
                  >
                    Next <ChevronRight size={20} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 bg-[#C89B3C] text-white px-8 py-3 font-medium hover:bg-[#a8852f] transition-colors disabled:opacity-50"
                    data-testid="intake-submit"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Intake Form"}
                  </button>
                )}
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const StepHeading = ({ title, subtitle }) => (
  <div className="border-b border-slate-200 pb-4 mb-6">
    <h3 className="font-serif text-xl font-bold text-slate-900">{title}</h3>
    <p className="text-slate-500 text-sm">{subtitle}</p>
  </div>
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

const Step1 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="Contact Information" subtitle="How can we reach you?" />
    <Field label="Full Name" required>
      <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required className={inputCls} data-testid="intake-fullname" />
    </Field>
    <Field label="Email Address" required>
      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputCls} data-testid="intake-email" />
    </Field>
    <Field label="Confirm Email Address" required>
      <input type="email" name="confirmEmail" value={formData.confirmEmail} onChange={handleChange} required className={inputCls} data-testid="intake-confirm-email" />
    </Field>
    <Field label="Phone Number" required>
      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputCls} placeholder="(555) 123-4567" data-testid="intake-phone" />
    </Field>
    <Field label="City & State" required>
      <input type="text" name="cityState" value={formData.cityState} onChange={handleChange} required className={inputCls} placeholder="City, State" data-testid="intake-citystate" />
    </Field>
  </div>
);

const Step2 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="Military Service Information" subtitle="Tell us about your service" />
    <Field label="Branch of Service" required>
      <select name="branch" value={formData.branch} onChange={handleChange} required className={inputCls} data-testid="intake-branch">
        <option value="">Select branch</option>
        {["Army", "Marines", "Air Force", "Navy", "Coast Guard", "Space Force", "National Guard", "Reserves"].map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
      </select>
    </Field>
    <Field label="Dates of Service (Start – End)" required>
      <input type="text" name="datesOfService" value={formData.datesOfService} onChange={handleChange} required className={inputCls} placeholder="Example: 2008–2016" data-testid="intake-dates" />
    </Field>
    <Field label="MOS / Rate / AFSC" required>
      <input type="text" name="mosRateAfsc" value={formData.mosRateAfsc} onChange={handleChange} required className={inputCls} data-testid="intake-mos" />
    </Field>
    <Field label="Deployments or Significant Service Events">
      <textarea name="deployments" value={formData.deployments} onChange={handleChange} rows={3} className={`${inputCls} resize-none`} placeholder="Optional" data-testid="intake-deployments" />
    </Field>
  </div>
);

const Step3 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="VA Claim Status" subtitle="Your current VA claim situation" />
    <Field label="Have you filed a VA disability claim before?" required>
      <select name="filedBefore" value={formData.filedBefore} onChange={handleChange} required className={inputCls} data-testid="intake-filed">
        <option value="">Select one</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="In progress">In progress</option>
      </select>
    </Field>
    <Field label="Current VA Disability Rating (if applicable)">
      <input type="text" name="currentRating" value={formData.currentRating} onChange={handleChange} className={inputCls} placeholder="Example: 30%, 70%, 90%" data-testid="intake-rating" />
    </Field>
    <Field label="What are you seeking help with?" required>
      <div className="space-y-2">
        {["New VA disability claim", "Claim increase", "Supplemental claim", "Higher-Level Review (HLR)", "Denied claim review", "C&P exam preparation", "Record review only", "Not sure / need guidance"].map((option) => (
          <label key={option} className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
            <input type="checkbox" name="seekingHelp" value={option} checked={formData.seekingHelp.includes(option)} onChange={handleChange} className="w-4 h-4" />
            <span className="text-slate-700 text-sm">{option}</span>
          </label>
        ))}
      </div>
    </Field>
  </div>
);

const Step4 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="Conditions & Symptoms" subtitle="What conditions do you need help with?" />
    <Field label="List the conditions you want help with (physical and/or mental health)" required>
      <textarea name="conditions" value={formData.conditions} onChange={handleChange} required rows={6} className={`${inputCls} resize-none`} data-testid="intake-conditions" />
    </Field>
  </div>
);

const Step5 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="Documentation" subtitle="Your medical and service records" />
    <Field label="Do you have copies of your medical and service records?" required>
      <select name="hasRecords" value={formData.hasRecords} onChange={handleChange} required className={inputCls} data-testid="intake-records">
        <option value="">Select one</option>
        <option value="Yes">Yes</option>
        <option value="Some">Some</option>
        <option value="No">No</option>
        <option value="Not sure">Not sure</option>
      </select>
    </Field>
    <Field label="Are you currently receiving VA medical care?" required>
      <select name="receivingVaCare" value={formData.receivingVaCare} onChange={handleChange} required className={inputCls} data-testid="intake-vacare">
        <option value="">Select one</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="Private Care">Private Care</option>
        <option value="No Insurance">No Insurance</option>
      </select>
    </Field>
  </div>
);

const Step6 = ({ formData, handleChange }) => (
  <div className="space-y-5">
    <StepHeading title="Timeline & Acknowledgments" subtitle="Final step — please review and consent" />
    <Field label="How soon are you looking to move forward?" required>
      <select name="timeline" value={formData.timeline} onChange={handleChange} required className={inputCls} data-testid="intake-timeline">
        <option value="">Select one</option>
        <option value="Immediately">Immediately</option>
        <option value="Within 1-2 weeks">Within 1-2 weeks</option>
        <option value="Within a month">Within a month</option>
        <option value="Just gathering information">Just gathering information</option>
      </select>
    </Field>
    <Field label="I was referred to you by">
      <input type="text" name="referredBy" value={formData.referredBy} onChange={handleChange} className={inputCls} placeholder="Optional" data-testid="intake-referral" />
    </Field>
    <div className="bg-amber-50 border border-amber-200 p-4 mt-4">
      <label className="block text-sm font-bold text-slate-900 mb-3">Required Acknowledgments *</label>
      <div className="space-y-3">
        {[
          "I understand Noble Strategies Consulting is not affiliated with the Department of Veterans Affairs",
          "I understand this service is consultative and educational, not legal representation",
          "I understand no outcome or VA rating is guaranteed",
        ].map((ack) => (
          <label key={ack} className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="acknowledgments" value={ack} checked={formData.acknowledgments.includes(ack)} onChange={handleChange} className="w-4 h-4 mt-1" />
            <span className="text-sm text-slate-700">{ack}</span>
          </label>
        ))}
      </div>
    </div>
    <div className="bg-slate-100 p-4">
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" name="consentToContact" checked={formData.consentToContact} onChange={handleChange} className="w-4 h-4 mt-1" data-testid="intake-consent" />
        <span className="text-sm text-slate-700">
          <strong>Consent to Contact *</strong>
          <br />I consent to be contacted by Noble Strategies Consulting regarding my inquiry.
        </span>
      </label>
    </div>
  </div>
);

export default IntakeModal;
